/**
 * F60 — /ops/health: is the API healthy right now, when did it hurt, and which
 * route. Permission `ops_logs:read` (super_admin).
 *
 * F60b layout ("Lanes", plans/F60b_badges_and_health_ui.md): a verdict for the
 * last 15 min, four status tiles, a timeline of four lanes on one shared time
 * axis (traffic, failed requests, problem logs, latency on a log scale), and
 * the per-route table. Everything is reshaped from /stats + /latency — no extra
 * Logs Insights scans.
 *
 * Loads on open, on a range change and on Refresh only — never on a timer —
 * because each load runs ~4 Logs Insights scans (the API caches them for a
 * minute). Clicking a route row opens /ops/logs filtered to it.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  OPS_HTTP_METHODS,
  type OpsHttpMethod,
  type OpsLatencyReport,
  type OpsLogsScanMeta,
  type OpsLogsStats,
  type OpsRouteLatency,
} from '@bravemove/types';
import { Banner, Button, Chip, Skeleton, cn } from '@bravemove/ui-web';
import { adminOpsLogsApi, type OpsRange } from '../../api/opsLogs';
import { PageHeader } from '../../components/PageHeader';
import { HealthTiles, HealthTimeline, RouteLatencyTable, VerdictPanel } from './HealthParts';
import { OpsNav } from './OpsNav';
import { ScanMeta } from './ScanMeta';
import {
  HEALTH_PRESETS,
  errorKey,
  formatBin,
  healthVerdict,
  mergeHealthBins,
  paramsFromSearch,
  rangeForHealthPreset,
  readTimeZoneMode,
  roundMs,
  trimLeadingEmpty,
  type HealthPreset,
} from './opsHelpers';

interface Loaded {
  readonly range: OpsRange;
  readonly stats: OpsLogsStats;
  readonly latency: OpsLatencyReport;
  readonly meta: OpsLogsScanMeta;
}

export function OpsHealthPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [timeZone] = useState(() => readTimeZoneMode());
  const [preset, setPreset] = useState<HealthPreset>('24h');
  const [runId, setRunId] = useState(0);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const range = rangeForHealthPreset(preset);
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const [stats, latency] = await Promise.all([
          adminOpsLogsApi.getStats(range),
          adminOpsLogsApi.getLatency(range),
        ]);
        if (cancelled) return;
        setLoaded({
          range,
          stats: stats.data,
          latency: latency.data,
          meta: {
            bytesScanned: stats.meta.bytesScanned + latency.meta.bytesScanned,
            recordsScanned: stats.meta.recordsScanned + latency.meta.recordsScanned,
            cached: stats.meta.cached && latency.meta.cached,
          },
        });
      } catch (err) {
        if (!cancelled) setError(errorKey(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [preset, runId]);

  // Bins merged once per load (not per render) so the memoised lanes keep
  // their data across hover updates.
  const view = useMemo(() => {
    if (!loaded) return null;
    const binSeconds = loaded.stats.binSeconds;
    const { bins, dataFrom } = trimLeadingEmpty(mergeHealthBins(loaded.stats, loaded.latency));
    const verdict = healthVerdict(bins, loaded.range.to, binSeconds);
    const start = Date.parse(bins[0]?.t ?? loaded.range.from);
    const rangeMinutes = Math.max(1, (Date.parse(loaded.range.to) - start) / 60_000);
    return { binSeconds, bins, dataFrom, verdict, rangeMinutes };
  }, [loaded]);

  const ms = (value: number | null) =>
    value === null
      ? t('admin:opsLogs.health.none')
      : t('admin:opsLogs.units.ms', { value: roundMs(value) });

  const openRoute = (row: OpsRouteLatency) => {
    const range = loaded?.range ?? rangeForHealthPreset(preset);
    const params = paramsFromSearch({
      preset: 'custom',
      from: range.from,
      to: range.to,
      filters: { route: row.route, ...(isMethod(row.method) ? { method: row.method } : {}) },
    });
    navigate(`/ops/logs?${params.toString()}`);
  };

  return (
    <div className="space-y-5" data-testid="ops-health-page">
      <PageHeader
        eyebrow={t('admin:opsLogs.eyebrow')}
        title={t('admin:opsLogs.health.title')}
        subtitle={t('admin:opsLogs.health.subtitle')}
        actions={
          <Button
            variant="secondary"
            size="sm"
            loading={loading}
            onClick={() => setRunId((n) => n + 1)}
            data-testid="ops-health-refresh"
          >
            {t('admin:opsLogs.health.refresh')}
          </Button>
        }
      />

      <OpsNav />

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {HEALTH_PRESETS.map((p) => (
            <Chip
              key={p}
              selected={preset === p}
              onClick={() => setPreset(p)}
              data-testid={`ops-health-preset-${p}`}
            >
              {t(`admin:opsLogs.presets.${p}`)}
            </Chip>
          ))}
        </div>
        {loaded ? <ScanMeta meta={loaded.meta} /> : null}
      </div>

      {error ? (
        <div data-testid="ops-health-error">
          <Banner tone={error === 'OPS_LOGS_NOT_CONFIGURED' ? 'warning' : 'danger'}>
            {t(`admin:opsLogs.errors.${error}`)}
          </Banner>
        </div>
      ) : null}

      {loading && !loaded ? (
        <div className="space-y-4" data-testid="ops-health-loading">
          <Skeleton className="h-24 w-full rounded-xl" />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }, (_, i) => (
              <Skeleton key={i} className="h-36 w-full rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      ) : null}

      {loaded && view ? (
        // A refetch keeps the previous render, dimmed, instead of a skeleton flash.
        <div
          className={cn('space-y-5 transition-opacity', loading && 'opacity-60')}
          aria-busy={loading}
        >
          <VerdictPanel
            verdict={view.verdict}
            asOf={formatBin(loaded.range.to, 60, timeZone, locale)}
            ms={ms}
          />
          <HealthTiles
            bins={view.bins}
            overall={loaded.latency.overall}
            recentErrors={view.verdict.errors}
            rangeMinutes={view.rangeMinutes}
            ms={ms}
          />
          <HealthTimeline
            bins={view.bins}
            binSeconds={view.binSeconds}
            dataFrom={view.dataFrom}
            timeZone={timeZone}
            ms={ms}
          />
          <RouteLatencyTable
            routes={loaded.latency.routes}
            truncated={loaded.latency.routesTruncated}
            onOpen={openRoute}
          />
        </div>
      ) : null}
    </div>
  );
}

function isMethod(value: string): value is OpsHttpMethod {
  return (OPS_HTTP_METHODS as readonly string[]).includes(value);
}
